import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { getApiBase, getToken } from './utils'

export const api = axios.create()

/** Détail d'erreur lisible (FastAPI : string, objet `{ code }`, ou liste de validation). */
export function formatApiErrorDetail(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const raw = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof raw === 'string') return raw
    if (Array.isArray(raw)) {
      const parts = raw.map((x: unknown) => {
        if (typeof x === 'object' && x !== null && 'msg' in x) {
          return String((x as { msg: unknown }).msg)
        }
        return typeof x === 'string' ? x : JSON.stringify(x)
      })
      const joined = parts.filter(Boolean).join('; ')
      if (joined) return joined
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>
      if (typeof o.message === 'string') return o.message
      if (typeof o.code === 'string') return o.code
      try {
        return JSON.stringify(raw)
      } catch {
        /* ignore */
      }
    }
    const st = err.response?.status
    if (st) {
      const txt = err.response?.statusText
      return `HTTP ${st}${txt ? ` ${txt}` : ''}`
    }
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

api.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = getApiBase()
  }
  const tok = getToken()
  if (tok) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${tok}`
  }
  const wid = typeof window !== 'undefined' ? localStorage.getItem('current_workspace_id') : null
  if (wid) {
    const raw = config.url || ''
    const path = raw.split('?')[0] || ''
    if (path === '/models') {
      config.headers = config.headers || {}
      ;(config.headers as Record<string, string>)['X-Workspace-Id'] = wid
    }
  }
  return config
})

// Auto-refresh handling
let isRefreshing = false
let pendingQueue: Array<{resolve: (value: unknown) => void, reject: (reason?: any) => void, config: AxiosRequestConfig}> = []

async function refreshAccessToken(): Promise<string> {
  // FastAPI-Users n'utilise pas de refresh tokens
  // En cas d'expiration du token, on redirige vers login
  // SAUF si on est sur /verify-email (pour éviter race condition pendant l'auto-login)
  const currentPath = window.location.pathname
  if (currentPath === '/verify-email') {
    throw new Error('Token refresh skipped during email verification')
  }
  
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
  localStorage.removeItem('current_workspace_id')
  window.location.href = '/login'
  throw new Error('Token expired - redirecting to login')
}

function processQueue(error: any, token: string | null) {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else {
      // retry the original request with new token
      if (token && config && config.headers) {
        (config.headers as any)['Authorization'] = `Bearer ${token}`
      }
      resolve(api.request(config))
    }
  })
  pendingQueue = []
}

// Debug + auto refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      if ((response?.config?.url || '').includes('/chat')) {
        // eslint-disable-next-line no-console
        console.info('[API] /chat response', response.status, response.data)
      }
    } catch {}
    return response
  },
  async (error: AxiosError) => {
    try {
      const url = (error?.config as any)?.url || ''
      const status = error?.response?.status

      // If Unauthorized, try to refresh once
      if (status === 401) {
        const originalRequest = error.config as AxiosRequestConfig
        if (!originalRequest) throw error

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject, config: originalRequest })
          })
        }

        isRefreshing = true
        try {
          const newToken = await refreshAccessToken()
          processQueue(null, newToken)
          isRefreshing = false
          // Retry current request
          originalRequest.headers = originalRequest.headers || {}
          ;(originalRequest.headers as any)['Authorization'] = `Bearer ${newToken}`
          return api.request(originalRequest)
        } catch (e) {
          processQueue(e, null)
          isRefreshing = false
          // clear tokens and reject
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('current_workspace_id')
          return Promise.reject(e)
        }
      }

      // Skip logging for non-critical Stripe endpoints when service unavailable
      const isStripeEndpoint = url.includes('/stripe/circuit-status') || url.includes('/stripe/dlq')
      const isServiceUnavailable = status === 503
      
      if (isStripeEndpoint && isServiceUnavailable) {
        // Silently skip - Stripe not configured
      } else if (url.includes('/chat')) {
        // eslint-disable-next-line no-console
        console.error('[API] /chat error', status, error?.response?.data || error?.message)
      } else {
        // eslint-disable-next-line no-console
        console.error('[API] error', url, status, error?.response?.data || error?.message)
      }
    } catch {}
    return Promise.reject(error)
  }
)

// ============================================================================
// System Prompts API (Admin Only)
// ============================================================================

export interface SystemPrompt {
  id: number
  name: string
  slug: string
  description: string | null
  content: string
  is_default: boolean
  is_active: boolean
  version: number
  created_at: string
  updated_at: string
  created_by: number | null
  updated_by: number | null
}

export interface SystemPromptListItem {
  id: number
  name: string
  slug: string
  description: string | null
  is_default: boolean
  is_active: boolean
  version: number
  content_preview: string
  updated_at: string
}

export interface SystemPromptHistory {
  id: number
  prompt_id: number
  content: string
  version: number
  changed_by: number | null
  changed_at: string
  change_reason: string | null
}

export interface SystemPromptCreate {
  name: string
  description?: string | null
  content: string
  is_default?: boolean
}

export interface SystemPromptUpdate {
  name?: string
  description?: string | null
  content?: string
  is_default?: boolean
  is_active?: boolean
  change_reason?: string
}

export const systemPromptsApi = {
  list: async (includeInactive = false): Promise<{ items: SystemPromptListItem[]; total: number }> => {
    const res = await api.get('/system-prompts', { params: { include_inactive: includeInactive } })
    return res.data
  },

  get: async (id: number): Promise<SystemPrompt> => {
    const res = await api.get(`/system-prompts/${id}`)
    return res.data
  },

  create: async (data: SystemPromptCreate): Promise<SystemPrompt> => {
    const res = await api.post('/system-prompts', data)
    return res.data
  },

  update: async (id: number, data: SystemPromptUpdate): Promise<SystemPrompt> => {
    const res = await api.put(`/system-prompts/${id}`, data)
    return res.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/system-prompts/${id}`)
  },

  getHistory: async (id: number, limit = 50): Promise<{ items: SystemPromptHistory[]; total: number }> => {
    const res = await api.get(`/system-prompts/${id}/history`, { params: { limit } })
    return res.data
  },

  restore: async (id: number, version: number): Promise<SystemPrompt> => {
    const res = await api.post(`/system-prompts/${id}/restore/${version}`)
    return res.data
  },
}

// ============================================================================
// Memory API
// ============================================================================

export interface MemoryScope {
  id: number
  tenant_id: number
  name: string
  slug: string
  description: string | null
  scope_type: 'tenant' | 'end_user'
  end_user_id: number | null
  is_active: boolean
  created_at: string
  updated_at: string | null
}

export interface MemoryScopeCreate {
  name: string
  slug?: string
  description?: string
  scope_type?: 'tenant' | 'end_user'
  end_user_id?: number
}

export interface MemoryScopeUpdate {
  name?: string
  description?: string
  is_active?: boolean
}

export interface MemoryFact {
  id: number
  tenant_id: number
  memory_scope_id: number
  end_user_id: number | null
  category: string
  subcategory: string | null
  fact_text: string
  fact_summary: string | null
  source_type: string
  source_ref: string | null
  valid_from: string
  valid_until: string | null
  confidence_level: 'auto' | 'verified' | 'deprecated'
  is_active: boolean
  is_redacted: boolean
  tags: string[]
  priority: number
  usage_count: number
  last_used_at: string | null
  created_at: string
  updated_at: string | null
}

export interface MemoryFactCreate {
  memory_scope_id: number
  category: string
  fact_text: string
  subcategory?: string
  fact_summary?: string
  source_type?: string
  source_ref?: string
  valid_until?: string
  confidence_level?: 'auto' | 'verified' | 'deprecated'
  tags?: string[]
  priority?: number
}

export interface MemoryFactUpdate {
  fact_text?: string
  fact_summary?: string
  category?: string
  subcategory?: string
  confidence_level?: 'auto' | 'verified' | 'deprecated'
  valid_until?: string
  is_active?: boolean
  tags?: string[]
  priority?: number
}

export interface MemoryCategory {
  id: number
  tenant_id: number
  name: string
  slug: string
  description: string | null
  default_ttl_days: number | null
  color: string
  icon: string | null
  is_system: boolean
  is_active: boolean
  created_at: string
  updated_at: string | null
}

export interface MemorySearchParams {
  query: string
  scope_ids?: number[]
  categories?: string[]
  top_k?: number
  min_confidence?: 'auto' | 'verified'
  include_deprecated?: boolean
}

export interface MemorySearchResult {
  fact: MemoryFact
  score: number
  scope_name: string
}

export const memoryApi = {
  // Scopes
  listScopes: async (): Promise<{ items: MemoryScope[]; total: number }> => {
    const res = await api.get('/memory/scopes')
    return res.data
  },

  getScope: async (id: number): Promise<MemoryScope> => {
    const res = await api.get(`/memory/scopes/${id}`)
    return res.data
  },

  createScope: async (data: MemoryScopeCreate): Promise<MemoryScope> => {
    const res = await api.post('/memory/scopes', data)
    return res.data
  },

  updateScope: async (id: number, data: MemoryScopeUpdate): Promise<MemoryScope> => {
    const res = await api.patch(`/memory/scopes/${id}`, data)
    return res.data
  },

  deleteScope: async (id: number): Promise<void> => {
    await api.delete(`/memory/scopes/${id}`)
  },

  // Facts
  listFacts: async (params?: { scope_id?: number; category?: string; limit?: number; offset?: number }): Promise<{ items: MemoryFact[]; total: number }> => {
    const res = await api.get('/memory/facts', { params })
    return res.data
  },

  getFact: async (id: number): Promise<MemoryFact> => {
    const res = await api.get(`/memory/facts/${id}`)
    return res.data
  },

  createFact: async (data: MemoryFactCreate): Promise<MemoryFact> => {
    const res = await api.post('/memory/facts', data)
    return res.data
  },

  updateFact: async (id: number, data: MemoryFactUpdate): Promise<MemoryFact> => {
    const res = await api.patch(`/memory/facts/${id}`, data)
    return res.data
  },

  deleteFact: async (id: number): Promise<void> => {
    await api.delete(`/memory/facts/${id}`)
  },

  // Categories
  listCategories: async (): Promise<{ items: MemoryCategory[]; total: number }> => {
    const res = await api.get('/memory/categories')
    return res.data
  },

  // Search
  search: async (params: MemorySearchParams): Promise<{ results: MemorySearchResult[]; total: number }> => {
    const res = await api.post('/memory/search', params)
    return res.data
  },

  // Agent-Scope associations
  getAgentScopes: async (agentId: number): Promise<number[]> => {
    const res = await api.get(`/agents/${agentId}/memory-scopes`)
    return res.data
  },

  updateAgentScopes: async (agentId: number, scopeIds: number[]): Promise<void> => {
    await api.put(`/agents/${agentId}/memory-scopes`, scopeIds)
  },
}

// ============================================================================
// End-Users API
// ============================================================================

export interface EndUser {
  id: number
  tenant_id: number
  external_id: string | null
  external_email: string | null
  external_username: string | null
  display_name: string | null
  metadata: Record<string, any>
  created_at: string
  updated_at: string | null
  last_seen_at: string | null
}

export interface EndUserStats {
  end_user_id: number
  display_name: string | null
  external_id: string | null
  external_email: string | null
  created_at: string | null
  last_seen_at: string | null
  memory_scopes_count: number
  memory_facts_count: number
}

export interface EndUserCreateRequest {
  external_id: string
  external_email?: string
  external_username?: string
  display_name?: string
  metadata?: Record<string, any>
}

export interface EndUserUpdateRequest {
  display_name?: string
  metadata?: Record<string, any>
}

export const endUsersApi = {
  // List
  list: async (params?: { limit?: number; offset?: number; search?: string }): Promise<{ items: EndUser[]; total: number }> => {
    const res = await api.get('/api/end-users', { params })
    return res.data
  },

  // Get
  get: async (id: number): Promise<EndUser> => {
    const res = await api.get(`/api/end-users/${id}`)
    return res.data
  },

  // Create
  create: async (data: EndUserCreateRequest): Promise<EndUser> => {
    const res = await api.post('/api/end-users', data)
    return res.data
  },

  // Update
  update: async (id: number, data: EndUserUpdateRequest): Promise<EndUser> => {
    const res = await api.patch(`/api/end-users/${id}`, data)
    return res.data
  },

  // Delete
  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/end-users/${id}`)
  },

  // Stats
  getStats: async (id: number): Promise<EndUserStats> => {
    const res = await api.get(`/api/end-users/${id}/stats`)
    return res.data
  },

  // Export (RGPD)
  exportData: async (id: number): Promise<any> => {
    const res = await api.get(`/api/end-users/${id}/export`)
    return res.data
  },

  // Anonymize
  anonymize: async (id: number): Promise<void> => {
    await api.post(`/api/end-users/${id}/anonymize`)
  },
}
